// src/pages/public/Blogs/BlogsPage.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FaClock } from 'react-icons/fa'
import { debounce } from 'lodash' // You may need to install lodash or use a custom debounce

import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchBlogs } from '../../../redux/slices/blogsSlice.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import Newsletter from '../../../components/common/Common/NewsLetter.jsx'
import SEO from '../../../components/seo/SEO.jsx'
import { formatDate } from '../../../utils/formatDate.js'
import { LazyImage } from '../../../components/common/LazyImage.jsx'
import EmptyState from '../../../components/common/EmptyState.jsx'
import SkeletonCard from '../../../components/common/SkeletonCard.jsx'
import LoadMoreButton from '../../../components/common/LoadMoreButton.jsx'

import './BlogsPage.scss'

export default function BlogsPage() {
  const dispatch = useAppDispatch()

  const { blogs, loading, hasMore, categories, allBlogs } = useAppSelector(
    (state) => state.blogs
  )
  const { currentPage: blogsPage } = useAppSelector((state) => state.cms)

  const [filters, setFilters] = useState({
    category: 'all',
    tag: '',
    search: '',
  })

  // CMS hero section
  const getSectionData = (sectionKey) => {
    return blogsPage?.sections?.find((section) => section.key === sectionKey)?.data
  }
  const heroSection = getSectionData('hero')

  useEffect(() => {
    dispatch(fetchPageBySlug('blogs'))
    // Load initial blogs (first page)
    dispatch(fetchBlogs({ page: 1, limit: 12 }))
  }, [dispatch])

  // Memoized filtered blogs
  const filteredBlogs = useMemo(() => {
    if (!blogs?.blogs) return []

    return blogs.blogs.filter(blog => {
      // Category filter
      if (filters.category !== 'all') {
        const blogCategory = blog.hero?.type || blog.category
        if (blogCategory !== filters.category) return false
      }

      // Search filter (case-insensitive search in title, excerpt, and content)
      if (filters.search.trim()) {
        const searchTerm = filters.search.toLowerCase().trim()
        const searchableContent = [
          blog.title,
          blog.excerpt,
          blog.content?.plainText || '',
          blog.tags?.join(' ') || ''
        ].join(' ').toLowerCase()

        if (!searchableContent.includes(searchTerm)) return false
      }

      // Tag filter (if you have tags in blog object)
      if (filters.tag) {
        if (!blog.tags || !blog.tags.includes(filters.tag)) return false
      }

      return true
    })
  }, [blogs?.blogs, filters])

  // Debounced search handler
  const handleSearchChange = useCallback(
    debounce((value) => {
      setFilters(prev => ({ ...prev, search: value }))
    }, 300),
    []
  )

  const handleCategoryChange = (category) => {
    setFilters(prev => ({ ...prev, category }))
  }

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = Math.ceil((blogs?.totalBlogs || 0) / 12) + 1
      dispatch(
        fetchBlogs({
          page: nextPage,
          limit: 12,
          ...filters // Pass filters for server-side filtering when loading more
        })
      )
    }
  }

  const activeTab = filters.category || 'all'

  // Tabs from categories (v1-style)
  const tabs = ['all', ...(categories || [])].map((cat) => ({
    id: cat === 'all' ? 'all' : cat,
    label: cat === 'all' ? 'All' : cat,
  }))

  const totalCount = filteredBlogs.length

  return (
    <>
      <SEO
        title={blogsPage?.seoTitle || ''}
        description={
          blogsPage?.seoDescription ||
          ''
        }
      />

      <div className="blogs-page-root">
        {/* =========================
            HERO – v1 Insights style
           ========================= */}
        <section className="blogs-hero-section">
          <div className="blogs-hero-container">
            <motion.h1
              className="blogs-heading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {(heroSection?.heading || 'Blog & Insights').toUpperCase()}
            </motion.h1>

            <motion.p
              className="blogs-subheading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {heroSection?.subheading ||
                ''}
            </motion.p>
          </div>
        </section>

        {/* =========================
            FILTERS + GRID – v1 cards
           ========================= */}
        <section className="blogs-grid-section">
          <div className="blogs-grid-container">
            {/* Filters row (tabs + search + count) */}
            <motion.div
              className="filters-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="filters-row">
                {/* Tabs (categories) */}
                <div className="tab-scroll-container-parent">
                  <div className="tab-scroll-container">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        className={`tab-button ${activeTab === tab.id ? 'active' : ''
                          }`}
                        onClick={() => handleCategoryChange(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search + count */}
                <div className="blogs-search-wrapper">
                  <input
                    type="text"
                    placeholder="Search blogs..."
                    defaultValue={filters.search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="blogs-search-input"
                  />
                  <div className="blogs-count-text">
                    {totalCount} {totalCount === 1 ? 'article' : 'articles'} found
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Grid */}
            {loading && blogs?.blogs?.length === 0 ? (
              <div className="blogs-cards-grid">
                {[...Array(6)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : filteredBlogs.length > 0 ? (
              <>
                <motion.div className="blogs-cards-grid" layout>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`${filters.category}-${filters.search}`}
                      className="grid-content"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {filteredBlogs.map((blog, index) => (
                        <BlogCard key={blog._id} blog={blog} index={index} />
                      ))}
                    </motion.div>
                  </AnimatePresence>
                </motion.div>

                {/* Only show load more if we haven't filtered */}
                {filters.category === 'all' && !filters.search && !filters.tag && (
                  <LoadMoreButton
                    hasMore={hasMore}
                    loading={loading}
                    onLoadMore={loadMore}
                  />
                )}
              </>
            ) : (
              <EmptyState
                title="No Blog Posts Found"
                message="We couldn't find any blog posts matching your criteria. Try adjusting your filters."
                action="Clear Filters"
                onAction={() => {
                  setFilters({ category: 'all', tag: '', search: '' })
                }}
              />
            )}
          </div>
        </section>
      </div>
      <Newsletter />
    </>
  )
}

// ==========================
// Blog Card – v1 Insights style
// ==========================
function BlogCard({ blog, index }) {
  const dateStr = formatDate(blog.publishedAt || blog.createdAt)
  const imageSrc = blog.hero?.imageUrl
  const category = blog.hero?.type
  const readTime = blog.readTime
  const authorName = blog.author?.name
  const authorImage = blog.author?.imageUrl
  const excerpt = blog.excerpt

  return (
    <Link to={`/blog/${blog.slug}`} className="group block">
      <motion.div
        className="content-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        whileHover={{ y: -5 }}
        layout
      >
        {/* Image + category tag */}
        <div className="card-image-container">
          {imageSrc ? (
            <LazyImage
              src={imageSrc}
              alt={blog.title}
              className="card-image"
              placeholder={
                <div className="card-image skeleton-bg" />
              }
            />
          ) : (
            <div className="card-image skeleton-bg" />
          )}

          {category && <span className="category-tag">{category}</span>}
        </div>

        {/* Content */}
        <div className="card-content">
          {/* Meta row */}
          <div className="card-meta">
            <span className="meta-date">{dateStr}</span>
            {readTime && (
              <>
                <span className="meta-divider">•</span>
                <span className="meta-time">
                  <FaClock className="meta-icon" />
                  {readTime}
                </span>
              </>
            )}
          </div>

          {/* Title & excerpt */}
          <h3 className="card-heading" title={blog.title}>
            {blog.title}
          </h3>

          {excerpt && (
            <p className="card-description">
              {excerpt}
            </p>
          )}

          {/* Footer: author + CTA */}
          <div className="card-footer">
            {authorName && (
              <div className="card-author">
                {authorImage ? (
                  <LazyImage
                    src={authorImage}
                    alt={authorName}
                    className="author-avatar"
                    placeholder={
                      <div className="author-avatar skeleton-bg" />
                    }
                  />
                ) : (
                  <div className="author-avatar skeleton-bg" />
                )}
                <span className="author-name">{authorName}</span>
              </div>
            )}

            <div className="card-read-more">
              <span>Read Article</span>
              <svg
                className="read-more-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  )
}